"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExchangeWebSocket = exports.DataType = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const BaseTradingApi_1 = require("../TradingBot/BaseTradingApi");
const BaseWebSocket_1 = require("./BaseWebSocket");
const DataRequest_1 = __importDefault(require("./DataRequest"));
var DataType;
(function (DataType) {
    DataType["ticker"] = "tickers.price";
    DataType["selectedTicker"] = "selectedTicker";
    DataType["orderBook"] = "order.book";
    DataType["trade"] = "trades.history";
})(DataType = exports.DataType || (exports.DataType = {}));
class ExchangeWebSocket extends BaseWebSocket_1.BaseWebSocket {
    constructor(config) {
        super(config);
        this.useOrderBookAndTrade = false;
        this.ticker = new DataRequest_1.default(cachedData => {
            this.subject.next({ type: DataType.ticker, data: new Map(cachedData) });
        }, new Map());
        this.selectedTicker = new DataRequest_1.default(cachedData => {
            this.subject.next({ type: DataType.selectedTicker, data: cachedData });
        });
        this.orderBook = new DataRequest_1.default(cachedData => {
            this.subject.next({ type: DataType.orderBook, data: cachedData });
        });
        this.trade = new DataRequest_1.default(cachedData => {
            this.subject.next({ type: DataType.trade, data: cachedData.slice() });
        }, []);
        this.firstTickerTime = 0;
        this.exchange = config.exchange;
        this.useTicker = config.useTicker;
        this.useOrderBookAndTrade = config.useOrderBookAndTrade;
        this.startEmit();
        if (config.getDefaultPair) {
            config.getDefaultPair()
                .subscribe(data => {
                if (!this.pair) {
                    this.pair = data;
                }
                this.open();
            }, () => {
                this.open();
            });
        }
        else {
            this.open();
        }
    }
    startEmit() {
        if (this.emitSubscription) {
            return;
        }
        this.emitSubscription = (0, rxjs_1.interval)(500).subscribe(() => {
            this.checkDisconnect();
        });
    }
    checkDisconnect() {
        if (this.subscription && this.connectionState === trade_utils_lib_1.State.DONE && this.useTicker && this.ticker.checkDisconnect()) {
            this.subscribeTicker();
            this.ticker.afterReSubscribe();
            this.orderBook.afterReSubscribe();
            this.trade.afterReSubscribe();
        }
    }
    sendData(data) {
        try {
            if (this.webSocket) {
                this.webSocket.send(JSON.stringify(data));
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    setExchange(exchange) {
        if (this.exchange === exchange) {
            return;
        }
        this.exchange = exchange;
        this.ticker.resetData(new Map());
        this.selectedTicker.resetData(undefined);
        this.orderBook.resetData(undefined);
        this.trade.resetData([]);
        this.subscribeTicker();
        this.subscribeOrderBookAndTrade();
    }
    setPair(pair) {
        if (this.pair && pair.base === this.pair.base && pair.quote === this.pair.quote) {
            return;
        }
        this.pair = pair;
        const symbol = (0, BaseTradingApi_1.toSymbol)(pair.base, pair.quote);
        this.selectedTicker.resetData(this.ticker.cachedData.get(symbol));
        this.orderBook.resetData(undefined);
        this.trade.resetData([]);
        this.subscribeOrderBookAndTrade();
    }
    setUseOrderBookAndTrade(useOrderBookAndTrade) {
        if (this.useOrderBookAndTrade === useOrderBookAndTrade) {
            return;
        }
        this.useOrderBookAndTrade = useOrderBookAndTrade;
        if (useOrderBookAndTrade) {
            this.subscribeOrderBookAndTrade();
        }
        else {
            this.unSubscribeOrderBookAndTrade();
        }
    }
    getCachedTicker() {
        if (this.ticker.cachedData.size === 0) {
            return undefined;
        }
        return Array.from(this.ticker.cachedData.values());
    }
    setCachedTicker(cachedTicker) {
        if (cachedTicker && this.ticker.cachedData.size === 0) {
            this.handleTicker(cachedTicker, true);
        }
    }
    getTickerObservable() {
        if (!this.useTicker) {
            throw new Error("this websocket useTicker is false");
        }
        return this.createDataObservable(DataType.ticker, subscriber => {
            if (this.ticker.cachedData.size > 0) {
                subscriber.next(new Map(this.ticker.cachedData));
            }
        });
    }
    getTickerObservableByPair(base, quote) {
        if (!this.useTicker) {
            throw new Error("this websocket useTicker is false");
        }
        const symbol = (0, BaseTradingApi_1.toSymbol)(base, quote);
        let cachedTicker;
        return this.createDataObservable(DataType.ticker, subscriber => {
            if (this.ticker.cachedData.size > 0) {
                subscriber.next(new Map(this.ticker.cachedData));
            }
        })
            .pipe((0, operators_1.map)(map => {
            if (map.has(symbol)) {
                return map.get(symbol);
            }
            else {
                return undefined;
            }
        }))
            .pipe((0, operators_1.filter)(ticker => {
            if (!ticker || (cachedTicker && cachedTicker.latest === ticker.latest)) {
                return false;
            }
            cachedTicker = ticker;
            return true;
        }));
    }
    getSelectedTickerObservable() {
        if (!this.useTicker) {
            throw new Error("this websocket useTicker is false");
        }
        return this.createDataObservable(DataType.selectedTicker, subscriber => {
            subscriber.next(this.selectedTicker.cachedData);
        });
    }
    getOrderBookObservable() {
        return this.createDataObservable(DataType.orderBook, subscriber => {
            subscriber.next(this.orderBook.cachedData);
        });
    }
    getTradeObservable() {
        return this.createDataObservable(DataType.trade, subscriber => {
            subscriber.next(this.trade.cachedData.slice());
        });
    }
    subscribeTicker() {
        if (this.exchange && this.useTicker) {
            this.sendData({
                action: "subscribe",
                channel: DataType.ticker,
                data: {
                    exchange: this.exchange,
                },
            });
        }
    }
    subscribeOrderBookAndTrade() {
        if (this.exchange && this.pair && this.useOrderBookAndTrade) {
            this.sendData({
                action: "subscribe",
                channel: `${DataType.orderBook},${DataType.trade}`,
                data: [{
                        exchange: this.exchange,
                        base: this.pair.base,
                        quote: this.pair.quote,
                    }],
            });
        }
    }
    unSubscribeOrderBookAndTrade() {
        this.sendData({
            action: "subscribe",
            channel: `${DataType.orderBook},${DataType.trade}`,
            data: [],
        });
    }
    onOpen() {
        this.subscribeTicker();
        if (this.useOrderBookAndTrade) {
            this.subscribeOrderBookAndTrade();
        }
        else {
            if (this.pair) {
                this.unSubscribeOrderBookAndTrade();
            }
        }
    }
    open() {
        this.close();
        this.subscription = (0, trade_utils_lib_1.requestWithRetry)(() => {
            console.log("open exchange websocket");
            this.connectCount++;
            this.connectionState = trade_utils_lib_1.State.ING;
            if (this.firstConnectTime === 0) {
                this.firstConnectTime = Date.now();
            }
            this.ticker.resetTime();
            let url = `${(0, trade_utils_lib_1.getTradeDependencyInstance)().getRunTimeHost().webSocketHost}/stream`;
            if (this.exchange) {
                url += `?exchange=${this.exchange}`;
                if (this.pair) {
                    url += `&base=${this.pair.base}&quote=${this.pair.quote}`;
                }
            }
            return this.createWebSocket(url);
        }, { name: "ExchangeWebSocket" })
            .subscribe(data => {
            if (!data) {
                console.log("websocket no data");
                return;
            }
            try {
                switch (data.channel.split("|||")[0]) {
                    case DataType.ticker:
                        this.handleTicker(data.data);
                        break;
                    case DataType.orderBook:
                        this.handleOrderBook(data.data);
                        break;
                    case DataType.trade:
                        this.handleTrade(data.data);
                        break;
                }
            }
            catch (e) {
                console.log(e);
            }
        });
    }
    handleTicker(pushData, fromCache = false) {
        const dataRequest = this.ticker;
        const data = fromCache ? pushData : pushData.map(tickerInfo => ({
            exchange: tickerInfo.market,
            full_name: tickerInfo.full_name,
            symbol: (0, BaseTradingApi_1.toSymbol)(tickerInfo.base, tickerInfo.quote),
            base: tickerInfo.base,
            quote: tickerInfo.quote,
            latest: tickerInfo.price,
            fiat: tickerInfo.fiat,
            latestAmount: undefined,
            changePercent: tickerInfo.change_percent,
            prev24HPrice: tickerInfo.price - tickerInfo.change_percent * tickerInfo.price,
            high: tickerInfo.high,
            low: tickerInfo.low,
            baseVol: tickerInfo.volume,
            quoteVol: tickerInfo.volume_to,
        }));
        data.forEach(ticker => {
            if (ticker.exchange === this.exchange) {
                const oldTicker = dataRequest.cachedData.get(ticker.symbol);
                if (oldTicker) {
                    ticker.prePrice = oldTicker.latest;
                    if (ticker.latest === oldTicker.latest) {
                        ticker.isPriceUp = oldTicker.isPriceUp == undefined || oldTicker.isPriceUp === true;
                    }
                    else {
                        ticker.isPriceUp = ticker.latest > oldTicker.latest;
                    }
                }
                else {
                    ticker.isPriceUp = true;
                }
                if (this.pair && ticker.base === this.pair.base && ticker.quote === this.pair.quote) {
                    this.selectedTicker.setCacheData(ticker);
                    this.selectedTicker.emitData();
                }
                dataRequest.cachedData.set(ticker.symbol, ticker);
            }
        });
        dataRequest.setCacheData(dataRequest.cachedData);
        dataRequest.emitData();
        if (!fromCache && this.firstTickerTime === 0 && dataRequest.cachedData.size > 0) {
            this.firstTickerTime = Date.now();
            this.reportState();
        }
    }
    handleOrderBook(pushData) {
        const bids = [];
        const asks = [];
        let jsonEntry = pushData[0];
        if (jsonEntry.bids && jsonEntry.bids.length) {
            jsonEntry.bids.forEach(item => {
                const entry = {
                    price: parseFloat(item[0]),
                    quantity: parseFloat(item[1]),
                };
                bids.push(entry);
            });
        }
        if (jsonEntry.asks && jsonEntry.asks.length) {
            jsonEntry.asks.forEach(item => {
                const entry = {
                    price: parseFloat(item[0]),
                    quantity: parseFloat(item[1]),
                };
                asks.push(entry);
            });
        }
        const data = {
            base: jsonEntry.base.toUpperCase(),
            quote: jsonEntry.quote.toUpperCase(),
            bids: bids,
            asks: asks,
        };
        if (data.base === this.pair.base && data.quote === this.pair.quote) {
            this.orderBook.setCacheData(data);
            this.orderBook.emitData();
        }
    }
    handleTrade(pushData) {
        const data = pushData.map(item => ({
            base: item.base,
            quote: item.quote,
            symbol: item.base.toUpperCase() + "/" + item.quote.toUpperCase(),
            buyerMaker: item.buyer_maker,
            side: item.buyer_maker === true ? "buy" : "sell",
            timestamp: item.timestamp,
            price: item.price,
            amount: item.quantity,
            fee: 0,
            order: item.origin_id,
            id: item.origin_id,
        }));
        if (data.length > 0 && data[0].base === this.pair.base && data[0].quote === this.pair.quote) {
            this.trade.setCacheData(data);
            this.trade.emitData();
        }
    }
    reportState() {
        if (!this.config.stateCallback) {
            return;
        }
        let message = this.getBaseStateMessage();
        message += ` firstTickerTime: ${this.firstTickerTime}`;
        if (this.firstTickerTime > 0) {
            message += ` ticker cost: ${this.firstTickerTime - this.firstOpenTime}`;
        }
        this.config.stateCallback(this.connectionState, message);
    }
}
exports.ExchangeWebSocket = ExchangeWebSocket;
